import { lazy, type ReactNode, Suspense, useEffect, useState } from 'react'
import type { AppNavigateTarget } from './components/layout/navigate-context'
import type { SidebarRoute } from './components/layout/sidebar'
import { LandingPage } from './pages/landing'

const AppShell = lazy(() => import('./components/layout/app-shell'))

type InAppRoute = SidebarRoute
type Route = 'landing' | InAppRoute

function parsePathRoute(pathname: string): Route {
	if (pathname === '/home') return 'home'
	if (pathname === '/trade') return 'trade'
	if (pathname === '/borrow') return 'borrow'
	if (pathname === '/earn') return 'earn'
	if (pathname === '/settings') return 'settings'
	if (pathname === '/agent' || pathname === '/settings/agent') return 'agent'
	return 'landing'
}

function routeToPath(route: Route): string {
	if (route === 'home') return '/home'
	if (route === 'trade') return '/trade'
	if (route === 'borrow') return '/borrow'
	if (route === 'earn') return '/earn'
	if (route === 'settings') return '/settings'
	if (route === 'agent') return '/settings/agent'
	return '/'
}

/**
 * Minimal client-side router via the History API.
 *
 *   /          → landing (Web3-free)
 *   /home      → portfolio overview
 *   /trade     → Authorised Polymarket markets
 *   /borrow    → Borrow/Repay action card
 *   /earn      → LP Deposit/Withdraw
 *   /settings  → deferred placeholder
 *
 * Cloudflare Workers Static Assets is configured with
 * `not_found_handling: "single-page-application"` (see
 * apps/web/wrangler.jsonc), so the Worker serves index.html for any
 * unknown path and we resolve the route client-side from
 * `window.location.pathname` on first render + on every popstate.
 *
 * Internal navigation goes through `navigate()` which pushes to
 * history.state — the back/forward buttons + the in-app Sidebar both
 * end up firing popstate or pushState + setRoute, so the route state
 * stays in sync either way.
 *
 * Web3Provider (wagmi + RainbowKit + react-query) is mounted ONCE
 * inside the lazy-loaded `AppShell` that wraps the 5 in-app routes.
 * Landing stays free of the ~214 KB gzip web3 chunk. See
 * docs/spike-2026-05-15-bundle-size.md.
 */
export function App() {
	const [route, setRoute] = useState<Route>(() =>
		typeof window !== 'undefined'
			? parsePathRoute(window.location.pathname)
			: 'landing',
	)

	useEffect(() => {
		function onPopState() {
			setRoute(parsePathRoute(window.location.pathname))
		}
		window.addEventListener('popstate', onPopState)
		return () => window.removeEventListener('popstate', onPopState)
	}, [])

	function navigate(target: Route) {
		const path = routeToPath(target)
		if (window.location.pathname !== path) {
			window.history.pushState(null, '', path)
		}
		setRoute(target)
		window.scrollTo({ top: 0, behavior: 'instant' })
	}

	function navigateInApp(target: AppNavigateTarget) {
		navigate(target)
	}

	let page: ReactNode
	if (route === 'landing') {
		page = <LandingPage onEnter={() => navigate('home')} />
	} else {
		const inAppRoute: InAppRoute = route
		page = <AppShell route={inAppRoute} onNavigate={navigateInApp} />
	}

	return <Suspense fallback={null}>{page}</Suspense>
}
